/**
 * AdminOrderReminders.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from './ToastContainer';
import { getIcon } from '../utils/icons';
import LoadingSpinner from './LoadingSpinner';
import './AdminOrderReminders.css';

const AdminOrderReminders = () => {
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [schedule, setSchedule] = useState('0 9 * * 1');
  const [customSchedule, setCustomSchedule] = useState('');
  const [adminEmails, setAdminEmails] = useState(['']);
  const [adminBccEmails, setAdminBccEmails] = useState(['']);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardAnswers, setWizardAnswers] = useState({
    volume: '',
    processing: '',
    timing: ''
  });
  
  // Simplified preset schedules - wizard handles the rest
  const presetSchedules = [
    { 
      value: '0 17 * * *', 
      label: 'Every day at 5:00 PM', 
      description: 'End of business day',
      recommended: true
    },
    { 
      value: '0 9 * * *', 
      label: 'Every day at 9:00 AM', 
      description: 'Start of business day',
      recommended: true
    },
    { 
      value: '0 17 * * 1', 
      label: 'Every Monday at 5:00 PM', 
      description: 'Once per week',
      recommended: true
    },
    { 
      value: 'custom', 
      label: 'Custom Schedule', 
      description: 'Enter your own cron expression',
      recommended: false
    }
  ];

  // Check permissions
  const canManageReminders = currentUser && currentUser.roles && 
    (currentUser.roles.includes('admin') || currentUser.roles.includes('super_admin'));

  const fetchSchedulerStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        showError('Not authenticated. Please log in first.');
        setLoading(false);
        return;
      }

      const response = await fetch('/api/admin/orders/reminder-settings', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please log out and log back in.');
        }
        throw new Error('Failed to fetch scheduler settings');
      }

      const data = await response.json();
      setSchedule(data.schedule || '0 9 * * 1');
      // Handle both single email (backward compatibility) and array of emails
      if (data.adminEmail) {
        if (Array.isArray(data.adminEmail)) {
          setAdminEmails(data.adminEmail.length > 0 ? data.adminEmail : ['']);
        } else {
          setAdminEmails([data.adminEmail]);
        }
      } else {
        setAdminEmails(['']);
      }
      if (data.adminBcc) {
        if (Array.isArray(data.adminBcc)) {
          setAdminBccEmails(data.adminBcc.length > 0 ? data.adminBcc : ['']);
        } else {
          setAdminBccEmails([data.adminBcc]);
        }
      } else {
        setAdminBccEmails(['']);
      }
      setSchedulerStatus(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching scheduler settings:', error);
      showError('Failed to load reminder settings');
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    if (canManageReminders) {
      fetchSchedulerStatus();
    }
  }, [canManageReminders, fetchSchedulerStatus]);

  const handleScheduleChange = (value) => {
    setSchedule(value);
    if (value !== 'custom') {
      setCustomSchedule('');
    }
  };

  const handleSaveSettings = async () => {
    try {
      const scheduleToSave = schedule === 'custom' ? customSchedule : schedule;
      
      if (!scheduleToSave) {
        showError('Please select or enter a schedule');
        return;
      }

      // Validate emails - at least one email must be provided
      const validEmails = adminEmails.filter(email => email.trim() !== '');
      if (validEmails.length === 0) {
        showError('Please enter at least one admin email address');
        return;
      }

      const validBcc = adminBccEmails.filter(email => email.trim() !== '');

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = validEmails.filter(email => !emailRegex.test(email));
      if (invalidEmails.length > 0) {
        showError(`Invalid email format: ${invalidEmails.join(', ')}`);
        return;
      }

      const invalidBcc = validBcc.filter(email => !emailRegex.test(email));
      if (invalidBcc.length > 0) {
        showError(`Invalid BCC email format: ${invalidBcc.join(', ')}`);
        return;
      }

      const token = localStorage.getItem('token');
      
      if (!token) {
        showError('Not authenticated. Please log in first.');
        return;
      }

      const response = await fetch('/api/admin/orders/reminder-settings', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          schedule: scheduleToSave,
          adminEmail: validEmails,
          adminBcc: validBcc
        })
      });

      if (!response.ok) throw new Error('Failed to save settings');

      const data = await response.json();
      showSuccess('Reminder settings saved! Server will restart scheduler.');
      setSchedulerStatus(data);
    } catch (error) {
      console.error('Error saving settings:', error);
      showError('Failed to save settings: ' + error.message);
    }
  };

  const handleSendTestReminder = async () => {
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        showError('Not authenticated. Please log in first.');
        setSending(false);
        return;
      }

      const response = await fetch('/api/admin/orders/send-reminder', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to send reminder');

      const data = await response.json();
      showSuccess(`Test reminder sent! Found ${data.orderCount} open order${data.orderCount !== 1 ? 's' : ''}.`);
    } catch (error) {
      console.error('Error sending test reminder:', error);
      showError('Failed to send test reminder: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  const getRecommendedSchedule = () => {
    const { volume, processing, timing } = wizardAnswers;
    
    // Low volume (1-5 orders/week) - ship individually
    if (volume === 'low') {
      if (timing === 'morning') {
        return { schedule: '0 9 * * 1', label: 'Every Monday at 9:00 AM', reason: 'Weekly check for low volume' };
      }
      return { schedule: '0 17 * * 1', label: 'Every Monday at 5:00 PM', reason: 'Weekly check for low volume' };
    }
    
    // Medium volume (5-20 orders/week) - ship individually
    if (volume === 'medium') {
      if (processing === 'daily') {
        if (timing === 'morning') {
          return { schedule: '0 9 * * *', label: 'Every day at 9:00 AM', reason: 'Daily processing, morning review' };
        }
        return { schedule: '0 17 * * *', label: 'Every day at 5:00 PM', reason: 'Daily processing, end of day' };
      }
      // Few times per week
      if (timing === 'morning') {
        return { schedule: '0 9 * * 1,3,5', label: 'Mon, Wed, Fri at 9:00 AM', reason: 'Regular check-ins' };
      }
      return { schedule: '0 17 * * 1,3,5', label: 'Mon, Wed, Fri at 5:00 PM', reason: 'Regular check-ins' };
    }
    
    // High volume (20+ orders/week)
    if (volume === 'high') {
      if (timing === 'morning') {
        return { schedule: '0 9,17 * * *', label: 'Twice daily (9 AM & 5 PM)', reason: 'High volume needs frequent checks' };
      }
      return { schedule: '0 */6 * * *', label: 'Every 6 hours', reason: 'High volume, urgent processing' };
    }
    
    // Default recommendation
    return { schedule: '0 17 * * *', label: 'Every day at 5:00 PM', reason: 'Good starting point' };
  };

  const handleWizardAnswer = (question, answer) => {
    setWizardAnswers(prev => ({ ...prev, [question]: answer }));
    if (wizardStep < 3) {
      setWizardStep(wizardStep + 1);
    }
  };

  const applyWizardRecommendation = () => {
    const recommendation = getRecommendedSchedule();
    setSchedule(recommendation.schedule);
    setShowWizard(false);
    showSuccess(`Recommended: ${recommendation.label} - ${recommendation.reason}`);
  };

  const parseCronSchedule = (cronExpr) => {
    try {
      const parts = cronExpr.split(' ');
      if (parts.length !== 5) return 'Invalid cron expression';
      
      const [minute, hour, dayOfMonth, , dayOfWeek] = parts;
      
      // Simple parser for common patterns
      let description = '';
      
      // Day of week
      if (dayOfWeek !== '*') {
        const days = { '0': 'Sunday', '1': 'Monday', '2': 'Tuesday', '3': 'Wednesday', 
                       '4': 'Thursday', '5': 'Friday', '6': 'Saturday' };
        if (dayOfWeek.includes(',')) {
          const dayList = dayOfWeek.split(',').map(d => days[d]).join(', ');
          description += `Every ${dayList}`;
        } else if (dayOfWeek.includes('-')) {
          description += `Weekdays`;
        } else {
          description += `Every ${days[dayOfWeek]}`;
        }
      } else if (dayOfMonth !== '*') {
        if (dayOfMonth.includes(',')) {
          description += `On days ${dayOfMonth} of each month`;
        } else {
          description += `On day ${dayOfMonth} of each month`;
        }
      } else {
        description += 'Every day';
      }
      
      // Time
      if (hour.includes('*/')) {
        description += ` every ${hour.replace('*/', '')} hours`;
      } else {
        const hourNum = parseInt(hour);
        const minuteNum = parseInt(minute);
        const ampm = hourNum >= 12 ? 'PM' : 'AM';
        const displayHour = hourNum > 12 ? hourNum - 12 : (hourNum === 0 ? 12 : hourNum);
        description += ` at ${displayHour}:${minuteNum.toString().padStart(2, '0')} ${ampm}`;
      }
      
      return description;
    } catch (error) {
      return 'Custom schedule';
    }
  };

  if (!canManageReminders) {
    return (
      <div className="admin-order-reminders">
        <div className="access-denied">
          <h2>{getIcon('lock')} Access Denied</h2>
          <p>You don't have permission to manage order reminders.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-order-reminders">
        <div className="loading">
          <LoadingSpinner size="medium" message="Loading reminder settings..." />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-order-reminders">
      <div className="reminders-header">
        <h1>{getIcon('bell')} Order Reminder Settings</h1>
        <p>Configure automated email reminders for open orders</p>
      </div>

      {/* Current Status */}
      {schedulerStatus && (
        <div className="status-card">
          <div className="status-header">
            <h2>{getIcon('info')} Current Status</h2>
            <span className={`status-badge ${schedulerStatus.isRunning ? 'active' : 'inactive'}`}>
              {schedulerStatus.isRunning ? '🟢 Active' : '🔴 Inactive'}
            </span>
          </div>
          <div className="status-details">
            <div className="status-item">
              <strong>Schedule:</strong> {parseCronSchedule(schedulerStatus.schedule)}
            </div>
            <div className="status-item">
              <strong>Cron Expression:</strong> <code>{schedulerStatus.schedule}</code>
            </div>
            <div className="status-item">
              <strong>Admin Email{Array.isArray(schedulerStatus.adminEmail) && schedulerStatus.adminEmail.length > 1 ? 's' : ''} (To + CC):</strong> {
                Array.isArray(schedulerStatus.adminEmail) 
                  ? schedulerStatus.adminEmail.join(', ') 
                  : schedulerStatus.adminEmail
              }
            </div>
            {Array.isArray(schedulerStatus.adminBcc) && schedulerStatus.adminBcc.length > 0 && (
              <div className="status-item">
                <strong>BCC:</strong> {schedulerStatus.adminBcc.join(', ')}
              </div>
            )}
            {schedulerStatus.lastRun && (
              <div className="status-item">
                <strong>Last Run:</strong> {new Date(schedulerStatus.lastRun).toLocaleString()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick Setup Wizard */}
      <div className="wizard-card">
        <h2>{getIcon('magic')} Quick Setup (Recommended)</h2>
        <p className="card-description">
          <strong>Not sure which schedule to choose?</strong> Answer 3 quick questions and we'll recommend the perfect schedule for your workflow.
        </p>
        <button onClick={() => setShowWizard(true)} className="btn btn-wizard">
          ✨ Start Setup Wizard
        </button>
        <p className="wizard-hint">Takes less than 30 seconds!</p>
      </div>

      {/* Wizard Modal */}
      {showWizard && (
        <div className="wizard-modal-overlay" onClick={() => setShowWizard(false)}>
          <div className="wizard-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wizard-header">
              <h2>Setup Wizard - Step {wizardStep} of 3</h2>
              <button onClick={() => setShowWizard(false)} className="wizard-close">×</button>
            </div>
            
            {wizardStep === 1 && (
              <div className="wizard-step">
                <h3>📦 How many orders do you typically process per week?</h3>
                <p className="wizard-help">This helps us recommend the right frequency</p>
                <div className="wizard-options">
                  <button 
                    className={`wizard-option ${wizardAnswers.volume === 'low' ? 'selected' : ''}`}
                    onClick={() => handleWizardAnswer('volume', 'low')}
                  >
                    <strong>1-5 orders/week</strong>
                    <span>Low volume - ship individually as they come in</span>
                  </button>
                  <button 
                    className={`wizard-option ${wizardAnswers.volume === 'medium' ? 'selected' : ''}`}
                    onClick={() => handleWizardAnswer('volume', 'medium')}
                  >
                    <strong>5-20 orders/week</strong>
                    <span>Medium volume - regular processing schedule</span>
                  </button>
                  <button 
                    className={`wizard-option ${wizardAnswers.volume === 'high' ? 'selected' : ''}`}
                    onClick={() => handleWizardAnswer('volume', 'high')}
                  >
                    <strong>20+ orders/week</strong>
                    <span>High volume - need frequent reminders</span>
                  </button>
                </div>
              </div>
            )}
            
            {wizardStep === 2 && (
              <div className="wizard-step">
                <h3>⚙️ How often do you process and ship orders?</h3>
                <p className="wizard-help">When do you typically package and ship?</p>
                <div className="wizard-options">
                  <button 
                    className={`wizard-option ${wizardAnswers.processing === 'daily' ? 'selected' : ''}`}
                    onClick={() => handleWizardAnswer('processing', 'daily')}
                  >
                    <strong>Every day</strong>
                    <span>Process orders daily as they come in</span>
                  </button>
                  <button 
                    className={`wizard-option ${wizardAnswers.processing === 'few-times' ? 'selected' : ''}`}
                    onClick={() => handleWizardAnswer('processing', 'few-times')}
                  >
                    <strong>Few times per week</strong>
                    <span>Batch processing 2-3 times weekly</span>
                  </button>
                  <button 
                    className={`wizard-option ${wizardAnswers.processing === 'weekly' ? 'selected' : ''}`}
                    onClick={() => handleWizardAnswer('processing', 'weekly')}
                  >
                    <strong>Once a week</strong>
                    <span>Weekly batch processing</span>
                  </button>
                </div>
              </div>
            )}
            
            {wizardStep === 3 && (
              <div className="wizard-step">
                <h3>⏰ When do you prefer to review orders?</h3>
                <p className="wizard-help">What time works best for you?</p>
                <div className="wizard-options">
                  <button 
                    className={`wizard-option ${wizardAnswers.timing === 'morning' ? 'selected' : ''}`}
                    onClick={() => handleWizardAnswer('timing', 'morning')}
                  >
                    <strong>Morning (9:00 AM)</strong>
                    <span>Start the day with order review</span>
                  </button>
                  <button 
                    className={`wizard-option ${wizardAnswers.timing === 'evening' ? 'selected' : ''}`}
                    onClick={() => handleWizardAnswer('timing', 'evening')}
                  >
                    <strong>Evening (5:00 PM)</strong>
                    <span>End of day review before closing</span>
                  </button>
                </div>
                
                {wizardAnswers.volume && wizardAnswers.processing && wizardAnswers.timing && (
                  <div className="wizard-result">
                    <h4>✅ Recommended Schedule</h4>
                    <div className="wizard-recommendation">
                      <strong>{getRecommendedSchedule().label}</strong>
                      <p>{getRecommendedSchedule().reason}</p>
                    </div>
                    <div className="wizard-actions">
                      <button onClick={applyWizardRecommendation} className="btn btn-primary">
                        Apply Recommendation
                      </button>
                      <button onClick={() => setShowWizard(false)} className="btn btn-secondary">
                        Choose Manually
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Schedule Configuration */}
      <div className="settings-card">
        <h2>{getIcon('calendar')} Or Choose Manually</h2>
        <p className="card-description">Pick a preset schedule or create a custom one</p>

        <div className="schedule-options">
          {presetSchedules.map((preset) => (
            <label key={preset.value} className={`schedule-option ${schedule === preset.value ? 'selected' : ''} ${preset.recommended ? 'recommended' : ''}`}>
              <input
                type="radio"
                name="schedule"
                value={preset.value}
                checked={schedule === preset.value}
                onChange={(e) => handleScheduleChange(e.target.value)}
              />
              <div className="option-content">
                <div className="option-label">
                  {preset.label}
                  {preset.recommended && <span className="badge-recommended">⭐ Popular</span>}
                </div>
                <div className="option-description">{preset.description}</div>
              </div>
            </label>
          ))}
        </div>

        {/* Custom Schedule Input */}
        {schedule === 'custom' && (
          <div className="custom-schedule">
            <label htmlFor="customSchedule">
              <strong>Custom Cron Expression</strong>
              <span className="help-text">Format: minute hour day-of-month month day-of-week</span>
            </label>
            <input
              type="text"
              id="customSchedule"
              value={customSchedule}
              onChange={(e) => setCustomSchedule(e.target.value)}
              placeholder="0 17 * * *"
              className="input-field"
            />
            {customSchedule && (
              <div className="schedule-preview">
                <strong>Preview:</strong> {parseCronSchedule(customSchedule)}
              </div>
            )}
            <div className="cron-help">
              <strong>Examples:</strong>
              <ul>
                <li><code>0 17 * * *</code> - Every day at 5:00 PM</li>
                <li><code>0 9 * * 1</code> - Every Monday at 9:00 AM</li>
                <li><code>0 12 1,15 * *</code> - 1st and 15th of month at noon</li>
                <li><code>0 */6 * * *</code> - Every 6 hours</li>
              </ul>
            </div>
          </div>
        )}

        {/* Admin Emails */}
        <div className="email-setting">
          <label>
            <strong>Admin Email Addresses</strong>
            <span className="help-text">All listed addresses are CC&apos;d on each reminder. The first address is also used as the primary To recipient.</span>
          </label>
          <div className="emails-list">
            {adminEmails.map((email, index) => (
              <div key={index} className="email-input-row">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    const newEmails = [...adminEmails];
                    newEmails[index] = e.target.value;
                    setAdminEmails(newEmails);
                  }}
                  placeholder="admin@cranfordpub.ca"
                  className="input-field"
                />
                {adminEmails.length > 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      const newEmails = adminEmails.filter((_, i) => i !== index);
                      setAdminEmails(newEmails.length > 0 ? newEmails : ['']);
                    }}
                    className="btn-remove-email"
                    title="Remove email"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => setAdminEmails([...adminEmails, ''])}
              className="btn-add-email"
            >
              + Add Another Email
            </button>
          </div>
        </div>

        {/* BCC Emails */}
        <div className="email-setting">
          <label>
            <strong>BCC Email Addresses</strong>
            <span className="help-text">Optional hidden copies (e.g. archive or personal Gmail). Leave blank if not needed.</span>
          </label>
          <div className="emails-list">
            {adminBccEmails.map((email, index) => (
              <div key={index} className="email-input-row">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    const newEmails = [...adminBccEmails];
                    newEmails[index] = e.target.value;
                    setAdminBccEmails(newEmails);
                  }}
                  placeholder="cranfordpub@gmail.com"
                  className="input-field"
                />
                <button
                  type="button"
                  onClick={() => {
                    const newEmails = adminBccEmails.filter((_, i) => i !== index);
                    setAdminBccEmails(newEmails.length > 0 ? newEmails : ['']);
                  }}
                  className="btn-remove-email"
                  title="Remove BCC email"
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setAdminBccEmails([...adminBccEmails, ''])}
              className="btn-add-email"
            >
              + Add BCC Email
            </button>
          </div>
        </div>

        <div className="settings-actions">
          <button onClick={handleSaveSettings} className="btn btn-primary">
            {getIcon('save')} Save Settings
          </button>
        </div>
      </div>

      {/* Test Reminder */}
      <div className="test-card">
        <h2>{getIcon('send')} Test Reminder</h2>
        <p className="card-description">
          Send a test reminder email immediately to verify your settings
        </p>
        
        <button 
          onClick={handleSendTestReminder} 
          disabled={sending}
          className="btn btn-secondary"
        >
          {sending ? (
            <>
              <span className="spinner-small"></span> Sending...
            </>
          ) : (
            <>
              {getIcon('mail')} Send Test Reminder Now
            </>
          )}
        </button>
      </div>

      {/* Compact Information */}
      <div className="info-card-compact">
        <p>
          <strong>💡 How it works:</strong> The system checks for open <strong>live</strong> orders (pending/processing) and sends email reminders based on your schedule.
          Test mail orders and Stripe/PayPal sandbox checkouts are excluded.
          Orders are grouped by age (🔴 Urgent 7+ days, 🟡 3-6 days, 🟢 1-2 days, 🆕 New) with direct links to update status.
        </p>
      </div>
    </div>
  );
};

export default AdminOrderReminders;

